import { round } from 'mathjs'
import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoLeakDetectionValveJSON {
  type: GeckoFeatureModelType.LeakDetectionValve
  name: string
  surface_specs: {
    thickness: number | undefined
  }
  feature_geometry: {
    x: number
    y: number
    radius: number
  }
}

export const NewGeckoLeakDetectionValveJSON: GeckoLeakDetectionValveJSON = {
  type: GeckoFeatureModelType.LeakDetectionValve,
  name: 'New Leak Detection Valve',
  surface_specs: {
    thickness: undefined,
  },
  feature_geometry: {
    x: 0,
    y: 0,
    radius: 0,
  },
}

export class GeckoLeakDetectionValve extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.LeakDetectionValve
  readonly geometry: GeckoComponentCoords
  readonly radius: GeckoMeasurement
  readonly thickness: GeckoMeasurement | undefined

  private constructor(
    id: string,
    name: string,
    geometry: GeckoComponentCoords,
    radius: GeckoMeasurement,
    thickness: GeckoMeasurement | undefined
  ) {
    super(id)
    this.name = name
    this.geometry = geometry
    this.radius = radius
    this.thickness = thickness
  }

  public static CreateFromJSON(
    componentID: string,
    valveID: string,
    valveJSON: GeckoLeakDetectionValveJSON
  ): GeckoLeakDetectionValve {
    const featureGeometryData = valveJSON.feature_geometry as Record<
      string,
      number
    >
    const valveGeometry = GeckoComponentCoords.CreateFromValues(
      componentID,
      featureGeometryData.x,
      GeckoMeasurementUnit.Meters,
      featureGeometryData.y,
      GeckoMeasurementUnit.Meters
    )
    const valveRadius = GeckoMeasurement.CreateFromValue(
      featureGeometryData.radius as number,
      GeckoMeasurementUnit.Meters
    )

    const surfaceSpecs = valveJSON.surface_specs as Record<string, number>
    const valveThickness =
      surfaceSpecs.thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.thickness as number,
            GeckoMeasurementUnit.Meters
          )
        : undefined

    const valveName = parseString(valveJSON.name) ?? ''

    return new GeckoLeakDetectionValve(
      valveID,
      valveName,
      valveGeometry,
      valveRadius,
      valveThickness
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoLeakDetectionValveJSON>
  ): Map<string, GeckoLeakDetectionValve> {
    return Object.keys(recordsJSON).reduce((resultObj, valveID) => {
      resultObj.set(
        valveID,
        GeckoLeakDetectionValve.CreateFromJSON(
          componentID,
          valveID,
          recordsJSON[valveID]
        )
      )
      return resultObj
    }, new Map<string, GeckoLeakDetectionValve>())
  }

  public asJSON(): GeckoLeakDetectionValveJSON {
    return generateLeakDetectionValve(
      this.name,
      this.geometry.x,
      this.geometry.y,
      this.radius,
      this.thickness
    )
  }
}

export function generateLeakDetectionValve(
  name: string,
  x: GeckoMeasurement,
  y: GeckoMeasurement,
  radius: GeckoMeasurement,
  thickness: GeckoMeasurement | undefined
): GeckoLeakDetectionValveJSON {
  const valveJSON: GeckoLeakDetectionValveJSON = {
    type: GeckoFeatureModelType.LeakDetectionValve,
    name: name,
    feature_geometry: {
      x: round(x.toNumber(GeckoMeasurementUnit.Meters), 5),
      y: round(y.toNumber(GeckoMeasurementUnit.Meters), 5),
      radius: round(radius.toNumber(GeckoMeasurementUnit.Meters), 5),
    },
    surface_specs: {
      thickness:
        thickness !== undefined
          ? thickness.toNumber(GeckoMeasurementUnit.Meters)
          : undefined,
    },
  }

  return valveJSON
}

export const LeakDetectionValveFormFields = (
  valveJSON: GeckoLeakDetectionValveJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Leak Detection Valve Name',
      value: valveJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: valveJSON.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: valveJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'radius',
      label: 'Radius',
      value: valveJSON.feature_geometry.radius,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'thickness',
      label: 'Thickness',
      value: valveJSON.surface_specs.thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const LeakDetectionValveJSONFromFormFields = (
  formFields: FormField[]
) => {
  const valveThickness = getFormFieldByDataKey(formFields, 'thickness')
  return generateLeakDetectionValve(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'radius')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    valveThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          valveThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined
  )
}

export const LeakDetectionValveFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.LeakDetectionValve,
  title: 'Leak Detection Valves',
  singularName: 'Leak Detection Valve',
  fields: ['name', 'x', 'y', 'radius'],
}
